// src/app/components/admin/brand-record/brand-record-page.tsx
//
// Admin tools → Brand record (#131). One append-only record per client: every
// signal writes an entry (a read's beliefs and competitors, the confirmed truth
// set, the voice profile, monitoring snapshots, publications and what they
// moved, market findings, events), each with its source, date and confidence.
//
// Four views of the selected client's record:
//   Current       the newest entry per key, optionally as of a date
//   Timeline      every entry, newest recorded first, filtered by type/source
//   What changed  entries recorded since a date, each beside what it replaced
//   Add           the two hand-entered writers: a market finding (or a
//                 category read's list of them) and the manual deployment record
//
// Laid out on the admin portal's shell, like the Wind tunnel: clients in the
// left column, the record in the content column.

import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { BookMarked, ChevronRight, Loader2, PanelLeft, RefreshCw, X } from "lucide-react";

import {
  brandRecordApi,
  type BrandRecordView,
  type RecordChanges,
  type RecordConfidence,
  type RecordCustomer,
  type RecordEntry,
  type RecordEntryType,
} from "@/api/brand-record";
import { useResponsive } from "@/app/hooks/common/use-breakpoint";
import {
  Button, ErrorNote, Input, PageHeading, Section, Textarea, helpStyle, labelStyle, selectClass,
} from "../wind-tunnel/wind-tunnel-shared";

const ACCENT = "var(--alpha-teal)";
type Tab = "current" | "timeline" | "changes" | "add";
const TABS: { id: Tab; label: string }[] = [
  { id: "current", label: "Current" },
  { id: "timeline", label: "Timeline" },
  { id: "changes", label: "What changed" },
  { id: "add", label: "Add" },
];

// ── Formatting ──────────────────────────────────────────────────────────────

function pct(cell: any): string {
  const share = cell && typeof cell === "object" ? cell.share : cell;
  if (share === null || share === undefined) return "Not measured";
  return `${(Number(share) * 100).toFixed(1)}%`;
}

/** Admin only: the counts behind a share, e.g. " (81 of 1000)". */
function counts(cell: any): string {
  return cell && typeof cell === "object" && cell.answers ? ` (${cell.present ?? 0} of ${cell.answers})` : "";
}

function day(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function stamp(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

const RESULT_LABEL: Record<string, string> = {
  confirmed: "Confirmed",
  not_yet_moved: "Not yet moved",
  not_measured: "Not measured yet",
  no_target: "No target",
  retired: "Retired",
};

/** One line that says what an entry holds, per type. */
function summarize(e: RecordEntry): string {
  const v = e.value || {};
  if (e.retracted) return v.reason || "Withdrawn";
  switch (e.entry_type) {
    case "belief": {
      const trend = v.trend
        ? `, ${v.trend.delta >= 0 ? "+" : ""}${(v.trend.delta * 100).toFixed(1)} pts vs ${v.trend.from?.label}${v.trend.moved ? "" : " (inside the band)"}`
        : "";
      return `${v.name}: present in ${pct(v.presence)}${counts(v.presence)} of answers in ${v.read?.label}${trend}; primary ${pct(v.primary_when_present)} when present, ${pct(v.primary_when_absent)} when absent`;
    }
    case "competitor":
      return `${v.name}: named ${pct(v.named)}, top five ${pct(v.top5)}, primary ${pct(v.primary)} in ${v.read?.label}`;
    case "fact": {
      const values = (v.values || []).map((x: any) => x.value).join(", ");
      const label = v.attribute_label || v.attribute || e.entry_key;
      const subject = v.subject_type && v.subject_type !== "brand" ? ` (${v.subject_name})` : "";
      return `${label}${subject}: ${values}${v.version ? ` (truth set v${v.version.number})` : ""}${v.in_conflict ? ", in conflict on the site" : ""}`;
    }
    case "voice":
      return `Tone: ${v.tone || "not stated"}; never: ${(v.words_never || []).join(", ") || "none stated"}`;
    case "snapshot":
      return `Brand Monitoring run ${v.monitoring_run_id}: overall ${v.overall_score ?? "Not measured"}${v.tier ? `, ${v.tier}` : ""}`;
    case "market":
      return `${v.title}${v.source_document?.name ? ` (${v.source_document.name})` : ""}`;
    case "publication":
      return `${v.title} on ${v.destination}: ${RESULT_LABEL[v.result?.status] || v.result?.status || "Not measured yet"}`;
    case "event":
    case "outcome":
    default:
      return v.summary || e.entry_key;
  }
}

// ── Small pieces ────────────────────────────────────────────────────────────

const CONFIDENCE_TONE: Record<RecordConfidence, string> = {
  measured: "var(--alpha-teal)",
  confirmed: "var(--signal-green)",
  proposed: "var(--impact-medium, #d97706)",
};

function Pill({ children, color }: { children: ReactNode; color?: string }) {
  return (
    <span
      style={{
        padding: "1px 8px",
        borderRadius: 999,
        fontSize: 11.5,
        fontWeight: 600,
        color: color || "var(--muted-foreground)",
        border: `1px solid ${color || "var(--border)"}`,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  );
}

function EntryRow({ entry, typeLabel, showPrevious }: { entry: RecordEntry; typeLabel: string; showPrevious?: boolean }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderTop: "1px solid var(--border)", padding: "10px 0" }}>
      <button
        onClick={() => setOpen((o) => !o)}
        style={{ width: "100%", textAlign: "left", background: "none", border: "none", padding: 0, cursor: "pointer" }}
      >
        <div className="flex flex-wrap items-center gap-2" style={{ marginBottom: 4 }}>
          <Pill>{typeLabel}</Pill>
          <Pill color={CONFIDENCE_TONE[entry.confidence]}>{entry.confidence}</Pill>
          {entry.retracted && <Pill color="var(--infrared-coral)">withdrawn</Pill>}
          <span style={{ fontSize: 12, color: "var(--muted-foreground)" }}>
            About {day(entry.observed_at)} · recorded {stamp(entry.recorded_at)}
          </span>
        </div>
        <div style={{ fontSize: "var(--text-sm)", color: "var(--foreground)", lineHeight: 1.5 }}>{summarize(entry)}</div>
        <div style={{ fontSize: 12, color: "var(--muted-foreground)", marginTop: 2 }}>
          Source: {entry.source_label || entry.source_kind} ({entry.source_ref})
          {entry.recorded_by && entry.recorded_by !== "system" ? ` · by ${entry.recorded_by}` : ""}
          {entry.supersedes_id ? ` · supersedes #${entry.supersedes_id}` : ""} · #{entry.id}
        </div>
        {showPrevious && entry.previous && (
          <div style={{ fontSize: 12, color: "var(--muted-foreground)", marginTop: 4, paddingLeft: 10, borderLeft: "2px solid var(--border)" }}>
            Was: {summarize(entry.previous)} ({entry.previous.source_label || entry.previous.source_ref})
          </div>
        )}
      </button>
      {open && (
        <pre
          style={{
            marginTop: 8,
            padding: 12,
            borderRadius: 8,
            backgroundColor: "var(--muted-30)",
            fontSize: 12,
            lineHeight: 1.45,
            overflowX: "auto",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {JSON.stringify(entry.value, null, 2)}
        </pre>
      )}
    </div>
  );
}

function Loading() {
  return (
    <div className="flex items-center gap-2" style={{ fontSize: 13, color: "var(--muted-foreground)" }}>
      <Loader2 className="animate-spin" style={{ width: 16, height: 16 }} /> Loading…
    </div>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <label className="flex flex-col gap-1" style={{ minWidth: 0 }}>
      <span style={labelStyle}>{label}</span>
      {children}
      {hint && <span style={helpStyle}>{hint}</span>}
    </label>
  );
}

// ── Views ───────────────────────────────────────────────────────────────────

function CurrentView({ customerId, view, labels, onAsOf }: {
  customerId: number;
  view: BrandRecordView;
  labels: Record<string, string>;
  onAsOf: (asOf: string) => void;
}) {
  const [asOf, setAsOf] = useState(view.as_of ? view.as_of.slice(0, 10) : "");
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end gap-3">
        <Field label="As of" hint="The record as it stood for everything observed up to this date.">
          <Input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} style={{ maxWidth: 200 }} />
        </Field>
        <Button onClick={() => onAsOf(asOf)}>Show</Button>
        {view.as_of && <Button onClick={() => { setAsOf(""); onAsOf(""); }}>Today</Button>}
      </div>
      {view.types.map(({ id }) => {
        const rows = view.record[id] || [];
        return (
          <Section key={`${customerId}-${id}`} title={`${labels[id]} (${rows.length})`}>
            {rows.length === 0 ? (
              <p style={helpStyle}>Nothing recorded.</p>
            ) : (
              rows.map((e) => <EntryRow key={e.id} entry={e} typeLabel={labels[e.entry_type]} />)
            )}
          </Section>
        );
      })}
    </div>
  );
}

function TimelineView({ customerId, view, labels }: { customerId: number; view: BrandRecordView; labels: Record<string, string> }) {
  const [type, setType] = useState("");
  const [source, setSource] = useState("");
  const [rows, setRows] = useState<RecordEntry[] | null>(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    setRows(null);
    brandRecordApi.entries(customerId, { type, source, limit: 500 })
      .then((r) => setRows(r.entries))
      .catch((e) => setErr(String(e?.message || e)));
  }, [customerId, type, source]);

  return (
    <div className="flex flex-col gap-4">
      <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 200px), 1fr))" }}>
        <Field label="Type">
          <select className={selectClass} value={type} onChange={(e) => setType(e.target.value)}>
            <option value="">Every type</option>
            {view.types.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
        </Field>
        <Field label="Source">
          <select className={selectClass} value={source} onChange={(e) => setSource(e.target.value)}>
            <option value="">Every source</option>
            {view.source_kinds.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
          </select>
        </Field>
      </div>
      {err && <ErrorNote>{err}</ErrorNote>}
      <Section title="Timeline" hint="Every entry, newest recorded first. Nothing is overwritten: a new value supersedes the old one.">
        {rows === null ? <Loading /> : rows.length === 0 ? <p style={helpStyle}>Nothing recorded for this filter.</p> : (
          rows.map((e) => <EntryRow key={e.id} entry={e} typeLabel={labels[e.entry_type]} />)
        )}
        {rows && rows.length >= 500 && <p style={{ ...helpStyle, marginTop: 8 }}>Showing the newest 500. Narrow the filter to see older entries.</p>}
      </Section>
    </div>
  );
}

function ChangesView({ customerId, labels, types }: { customerId: number; labels: Record<string, string>; types: RecordEntryType[] }) {
  const weekAgo = useMemo(() => new Date(Date.now() - 7 * 864e5).toISOString().slice(0, 10), []);
  const [since, setSince] = useState(weekAgo);
  const [data, setData] = useState<RecordChanges | null>(null);
  const [err, setErr] = useState("");

  const load = useCallback(() => {
    setData(null);
    setErr("");
    brandRecordApi.changes(customerId, since).then(setData).catch((e) => setErr(String(e?.message || e)));
  }, [customerId, since]);

  useEffect(() => { load(); }, [customerId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end gap-3">
        <Field label="Since" hint="Entries recorded after this date, each beside the entry it replaced.">
          <Input type="date" value={since} onChange={(e) => setSince(e.target.value)} style={{ maxWidth: 200 }} />
        </Field>
        <Button onClick={load}>Show changes</Button>
      </div>
      {err && <ErrorNote>{err}</ErrorNote>}
      {data === null && !err ? <Loading /> : data && data.count === 0 ? (
        <Section title="What changed"><p style={helpStyle}>Nothing was recorded after {day(data.since)}.</p></Section>
      ) : data && types.filter((t) => data.by_type[t]?.length).map((t) => (
        <Section key={t} title={`${labels[t]} (${data.by_type[t]!.length})`}>
          {data.by_type[t]!.map((e) => <EntryRow key={e.id} entry={e} typeLabel={labels[t]} showPrevious />)}
        </Section>
      ))}
    </div>
  );
}

function AddView({ customerId, onSaved }: { customerId: number; onSaved: () => void }) {
  const today = new Date().toISOString().slice(0, 10);
  // Market finding
  const [title, setTitle] = useState("");
  const [finding, setFinding] = useState("");
  const [category, setCategory] = useState("");
  const [docName, setDocName] = useState("");
  const [docUrl, setDocUrl] = useState("");
  const [observed, setObserved] = useState(today);
  const [confidence, setConfidence] = useState<RecordConfidence>("proposed");
  const [importJson, setImportJson] = useState("");
  // Publication
  const [pubTitle, setPubTitle] = useState("");
  const [pubUrl, setPubUrl] = useState("");
  const [pubDate, setPubDate] = useState(today);
  const [pubDest, setPubDest] = useState("");
  const [pubBeliefs, setPubBeliefs] = useState("");
  const [pubQuestions, setPubQuestions] = useState("");
  const [pubAction, setPubAction] = useState("");
  const [pubWindTunnel, setPubWindTunnel] = useState("");
  const [pubNote, setPubNote] = useState("");
  // #133: a hand-entered publication record carries the platform's fields.
  const [pubApprover, setPubApprover] = useState("");
  const [pubContent, setPubContent] = useState("");
  const [pubTypes, setPubTypes] = useState("");
  const [pubDraft, setPubDraft] = useState("");
  const [pubDraftVersion, setPubDraftVersion] = useState("");
  const [pubItem, setPubItem] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const run = async (fn: () => Promise<{ written: number }>, done: () => void) => {
    setBusy(true);
    setErr("");
    setMsg("");
    try {
      const r = await fn();
      setMsg(r.written ? `Recorded ${r.written} entr${r.written === 1 ? "y" : "ies"}.` : "Already in the record; nothing new was written.");
      done();
      onSaved();
    } catch (e: any) {
      setErr(String(e?.message || e));
    } finally {
      setBusy(false);
    }
  };

  const saveFinding = () => run(
    () => brandRecordApi.saveFindings(customerId, [{
      title, finding, category, confidence, observed_at: observed,
      source_document: { name: docName, url: docUrl || undefined },
    }]),
    () => { setTitle(""); setFinding(""); },
  );

  const importFindings = () => {
    let parsed: any;
    try {
      parsed = JSON.parse(importJson);
    } catch {
      setErr("The import is not valid JSON.");
      return;
    }
    const list = Array.isArray(parsed) ? parsed : parsed?.findings;
    if (!Array.isArray(list)) {
      setErr("The import must be a list of findings, or {\"findings\": [...]}.");
      return;
    }
    run(() => brandRecordApi.saveFindings(customerId, list), () => setImportJson(""));
  };

  const ids = (text: string) => text.split(",").map((s) => s.trim()).filter(Boolean);

  const savePublication = () => run(
    () => brandRecordApi.recordPublication(customerId, {
      title: pubTitle,
      url: pubUrl || undefined,
      published_at: pubDate,
      destination: pubDest || undefined,
      targets: { belief_ids: ids(pubBeliefs), question_ids: ids(pubQuestions) },
      action_id: pubAction ? Number(pubAction) : undefined,
      wind_tunnel_run_id: pubWindTunnel ? Number(pubWindTunnel) : undefined,
      note: pubNote || undefined,
      approved_by: pubApprover || undefined,
      content: pubContent || undefined,
      question_types: ids(pubTypes),
      draft_id: pubDraft ? Number(pubDraft) : undefined,
      draft_version: pubDraft && pubDraftVersion ? Number(pubDraftVersion) : undefined,
      action_item_id: pubItem ? Number(pubItem) : undefined,
    }),
    () => { setPubTitle(""); setPubUrl(""); setPubNote(""); setPubContent(""); },
  );

  const grid = { display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 240px), 1fr))" } as const;

  return (
    <div className="flex flex-col gap-4">
      {err && <ErrorNote>{err}</ErrorNote>}
      {msg && <p style={{ fontSize: 13, color: "var(--signal-green)" }}>{msg}</p>}

      <Section title="Save a market finding" hint="A finding from a category read or competitive research, with the document it came from. Saving the same title again supersedes it.">
        <div style={grid}>
          <Field label="Title"><Input value={title} onChange={(e) => setTitle(e.target.value)} /></Field>
          <Field label="Category"><Input value={category} onChange={(e) => setCategory(e.target.value)} /></Field>
          <Field label="Source document"><Input value={docName} onChange={(e) => setDocName(e.target.value)} /></Field>
          <Field label="Document URL" hint="Optional."><Input value={docUrl} onChange={(e) => setDocUrl(e.target.value)} /></Field>
          <Field label="Date it is about"><Input type="date" value={observed} onChange={(e) => setObserved(e.target.value)} /></Field>
          <Field label="Confidence">
            <select className={selectClass} value={confidence} onChange={(e) => setConfidence(e.target.value as RecordConfidence)}>
              <option value="proposed">Proposed</option>
              <option value="measured">Measured</option>
              <option value="confirmed">Confirmed</option>
            </select>
          </Field>
        </div>
        <div style={{ marginTop: 12 }}>
          <Field label="Finding"><Textarea rows={3} value={finding} onChange={(e) => setFinding(e.target.value)} /></Field>
        </div>
        <div style={{ marginTop: 12 }}>
          <Button variant="primary" disabled={busy || !title.trim() || !docName.trim()} onClick={saveFinding}>Save finding</Button>
        </div>
      </Section>

      <Section title="Import a category read" hint='A JSON list of findings, each {"title", "finding", "figures": [{"label", "value"}], "source_document": {"name", "url"}, "observed_at", "confidence"}. All or nothing.'>
        <Textarea rows={6} value={importJson} onChange={(e) => setImportJson(e.target.value)} placeholder='[{"title": "...", "source_document": {"name": "..."}}]' />
        <div style={{ marginTop: 12 }}>
          <Button disabled={busy || !importJson.trim()} onClick={importFindings}>Import findings</Button>
        </div>
      </Section>

      <Section title="Record a publication" hint="A piece published outside the platform, with the same fields as a platform publish: who approved it, its content (for the hash), the draft and wind tunnel run if any. It appears in the record and on the series timeline, and the next read measures what it moved on the beliefs it targets.">
        <div style={grid}>
          <Field label="Title" hint="Optional when a draft is named."><Input value={pubTitle} onChange={(e) => setPubTitle(e.target.value)} /></Field>
          <Field label="Approved by" hint="The named person who approved it."><Input value={pubApprover} onChange={(e) => setPubApprover(e.target.value)} /></Field>
          <Field label="Content-studio draft" hint="Optional: its id fills beliefs, questions, run and approver."><Input inputMode="numeric" value={pubDraft} onChange={(e) => setPubDraft(e.target.value.replace(/\D/g, ""))} /></Field>
          <Field label="Draft version" hint="Optional: the latest if empty."><Input inputMode="numeric" value={pubDraftVersion} onChange={(e) => setPubDraftVersion(e.target.value.replace(/\D/g, ""))} /></Field>
          <Field label="Question types" hint="Comma separated, e.g. comparison, how_to."><Input value={pubTypes} onChange={(e) => setPubTypes(e.target.value)} /></Field>
          <Field label="Action item id" hint="Optional: marks that item Published."><Input inputMode="numeric" value={pubItem} onChange={(e) => setPubItem(e.target.value.replace(/\D/g, ""))} /></Field>
          <Field label="URL"><Input value={pubUrl} onChange={(e) => setPubUrl(e.target.value)} /></Field>
          <Field label="Published on"><Input type="date" value={pubDate} onChange={(e) => setPubDate(e.target.value)} /></Field>
          <Field label="Destination" hint="Site, blog, press, a partner page."><Input value={pubDest} onChange={(e) => setPubDest(e.target.value)} /></Field>
          <Field label="Targeted beliefs" hint="Belief ids, comma separated."><Input value={pubBeliefs} onChange={(e) => setPubBeliefs(e.target.value)} /></Field>
          <Field label="Targeted questions" hint="Question ids, comma separated."><Input value={pubQuestions} onChange={(e) => setPubQuestions(e.target.value)} /></Field>
          <Field label="Action id" hint="Optional: the action list item it answers."><Input inputMode="numeric" value={pubAction} onChange={(e) => setPubAction(e.target.value.replace(/\D/g, ""))} /></Field>
          <Field label="Wind tunnel run" hint="Optional: the run that tested it."><Input inputMode="numeric" value={pubWindTunnel} onChange={(e) => setPubWindTunnel(e.target.value.replace(/\D/g, ""))} /></Field>
        </div>
        <div style={{ marginTop: 12 }}>
          <Field label="Published content" hint="Paste what went out; the record keeps its hash. Not needed when a draft is named."><Textarea rows={4} value={pubContent} onChange={(e) => setPubContent(e.target.value)} /></Field>
        </div>
        <div style={{ marginTop: 12 }}>
          <Field label="Note"><Textarea rows={2} value={pubNote} onChange={(e) => setPubNote(e.target.value)} /></Field>
        </div>
        <div style={{ marginTop: 12 }}>
          <Button variant="primary"
            disabled={busy || !pubDate || !(pubTitle.trim() || pubDraft) || !(pubContent.trim() || pubDraft)}
            onClick={savePublication}>Record publication</Button>
        </div>
      </Section>
    </div>
  );
}

// ── Page ────────────────────────────────────────────────────────────────────

function RecordDetail({ customer, onChanged }: { customer: RecordCustomer; onChanged: () => void }) {
  const id = customer.customer_id;
  const [tab, setTab] = useState<Tab>("current");
  const [asOf, setAsOf] = useState("");
  const [view, setView] = useState<BrandRecordView | null>(null);
  const [err, setErr] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<Record<string, number | string> | null>(null);

  const load = useCallback(() => {
    setErr("");
    brandRecordApi.record(id, asOf || undefined).then(setView).catch((e) => setErr(String(e?.message || e)));
  }, [id, asOf]);

  useEffect(() => { load(); }, [load]);

  const sync = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const r = await brandRecordApi.sync(id);
      setSyncResult(r.written);
      load();
      onChanged();
    } catch (e: any) {
      setErr(String(e?.message || e));
    } finally {
      setSyncing(false);
    }
  };

  const labels = useMemo(
    () => Object.fromEntries((view?.types || []).map((t) => [t.id, t.label])) as Record<string, string>,
    [view],
  );
  const total = Object.values(view?.summary.by_type || {}).reduce((a, b) => a + (b || 0), 0);

  return (
    <div className="flex flex-col gap-5">
      <PageHeading
        icon={<BookMarked style={{ width: 20, height: 20, color: ACCENT }} />}
        title={customer.brand_name}
        subtitle={view ? (total
          ? `${total} entr${total === 1 ? "y" : "ies"}, last recorded ${stamp(view.summary.last_recorded_at)}`
          : "Nothing recorded yet") : undefined}
        right={
          <Button onClick={sync} disabled={syncing} title="Project every source into the record. Safe to repeat: what is already recorded writes nothing.">
            <span className="inline-flex items-center gap-2">
              {syncing ? <Loader2 className="animate-spin" style={{ width: 14, height: 14 }} /> : <RefreshCw style={{ width: 14, height: 14 }} />}
              Sync from sources
            </span>
          </Button>
        }
      />
      {err && <ErrorNote>{err}</ErrorNote>}
      {syncResult && (
        <p style={helpStyle}>
          Sync wrote: {Object.entries(syncResult).map(([k, v]) => `${k.replace(/_/g, " ")} ${v}`).join(", ")}.
        </p>
      )}
      {view && view.pending_reads.length > 0 && (
        <ErrorNote>
          Not yet in the record: {view.pending_reads.map((r) => r.label).join(", ")}. A read is recorded when its
          pipeline finishes; Sync from sources records it now.
        </ErrorNote>
      )}

      <div role="tablist" className="flex flex-wrap gap-1" style={{ borderBottom: "1px solid var(--border)" }}>
        {TABS.map((t) => (
          <button
            key={t.id}
            role="tab"
            aria-selected={tab === t.id}
            onClick={() => setTab(t.id)}
            style={{
              padding: "8px 14px",
              background: "none",
              border: "none",
              borderBottom: tab === t.id ? `2px solid ${ACCENT}` : "2px solid transparent",
              fontSize: "var(--text-sm)",
              fontWeight: tab === t.id ? 600 : 500,
              color: tab === t.id ? "var(--foreground)" : "var(--muted-foreground)",
              cursor: "pointer",
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {!view ? (!err && <Loading />) : tab === "current" ? (
        <CurrentView customerId={id} view={view} labels={labels} onAsOf={setAsOf} />
      ) : tab === "timeline" ? (
        <TimelineView customerId={id} view={view} labels={labels} />
      ) : tab === "changes" ? (
        <ChangesView customerId={id} labels={labels} types={view.types.map((t) => t.id)} />
      ) : (
        <AddView customerId={id} onSaved={() => { load(); onChanged(); }} />
      )}
    </div>
  );
}

export function BrandRecordPage() {
  const { isDesktop } = useResponsive();
  const [customers, setCustomers] = useState<RecordCustomer[] | null>(null);
  const [selected, setSelected] = useState<number | null>(null);
  const [filter, setFilter] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [err, setErr] = useState("");

  const loadCustomers = useCallback(() => {
    brandRecordApi.customers().then((r) => setCustomers(r.customers)).catch((e) => setErr(String(e?.message || e)));
  }, []);

  useEffect(() => { loadCustomers(); }, [loadCustomers]);
  useEffect(() => { if (isDesktop) setDrawerOpen(false); }, [isDesktop]);

  const shown = (customers || []).filter((c) => c.brand_name.toLowerCase().includes(filter.trim().toLowerCase()));
  const active = (customers || []).find((c) => c.customer_id === selected);

  const navContent = (
    <>
      <div style={{ height: 89, borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", gap: 10, padding: "0 24px", flexShrink: 0 }}>
        <div style={{ width: 32, height: 32, borderRadius: 8, backgroundColor: "var(--alpha-teal-10)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <BookMarked style={{ width: 16, height: 16, color: ACCENT }} />
        </div>
        <div style={{ minWidth: 0 }}>
          <h2 style={{ fontSize: "var(--text-lg)", fontWeight: "var(--font-weight-semibold)", lineHeight: 1.2 }}>Brand record</h2>
          <p style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }}>Admin · one record per client</p>
        </div>
        {!isDesktop && (
          <button
            onClick={() => setDrawerOpen(false)}
            aria-label="Close clients menu"
            style={{ marginLeft: "auto", padding: 6, borderRadius: 7, border: "1px solid var(--border)", backgroundColor: "var(--muted)", cursor: "pointer", color: "var(--muted-foreground)", display: "flex" }}
          >
            <X style={{ width: 14, height: 14 }} />
          </button>
        )}
      </div>
      <div style={{ padding: "16px 24px 8px" }}>
        <Input placeholder="Find a client" value={filter} onChange={(e) => setFilter(e.target.value)} />
      </div>
      <nav style={{ flex: 1, overflowY: "auto" }}>
        {customers === null && !err && <div style={{ padding: "8px 24px" }}><Loading /></div>}
        {shown.map((c) => {
          const isActive = c.customer_id === selected;
          return (
            <button
              key={c.customer_id}
              onClick={() => { setSelected(c.customer_id); setDrawerOpen(false); }}
              style={{
                width: "100%", display: "flex", alignItems: "center", gap: 12, padding: "10px 24px",
                backgroundColor: isActive ? "var(--muted)" : "transparent", border: "none",
                borderLeft: isActive ? `3px solid ${ACCENT}` : "3px solid transparent", cursor: "pointer", textAlign: "left",
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: "var(--text-sm)", fontWeight: isActive ? 600 : 500, color: isActive ? "var(--foreground)" : "var(--muted-foreground)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {c.brand_name}
                </div>
                <div style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }}>
                  #{c.customer_id} · {c.entries ? `${c.entries} entr${c.entries === 1 ? "y" : "ies"}` : "Empty"}
                </div>
              </div>
              {isActive && <ChevronRight style={{ width: 16, height: 16, color: ACCENT, flexShrink: 0 }} />}
            </button>
          );
        })}
      </nav>
    </>
  );

  return (
    <div data-kit className="flex" style={{ backgroundColor: "var(--background)" }}>
      {isDesktop ? (
        <aside style={{ width: 280, borderRight: "1px solid var(--border)", backgroundColor: "var(--background)", flexShrink: 0 }}>
          <div style={{ position: "sticky", top: 0, maxHeight: "100vh", overflowY: "auto", display: "flex", flexDirection: "column" }}>
            {navContent}
          </div>
        </aside>
      ) : (
        <>
          {drawerOpen && (
            <div onClick={() => setDrawerOpen(false)} style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.5)", zIndex: 45 }} aria-hidden="true" />
          )}
          <div
            style={{
              position: "fixed", top: 0, left: 0, height: "100dvh", width: "min(320px, 85vw)", zIndex: 46,
              backgroundColor: "var(--background)", borderRight: "1px solid var(--border)", display: "flex",
              flexDirection: "column", overflow: "hidden", transform: drawerOpen ? "translateX(0)" : "translateX(-100%)",
              transition: "transform 300ms ease-in-out",
            }}
            aria-hidden={!drawerOpen}
          >
            {navContent}
          </div>
        </>
      )}

      <main className="flex-1" style={{ backgroundColor: "var(--background)", minWidth: 0, paddingBottom: 24 }}>
        {!isDesktop && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "20px 16px 0", padding: "10px 12px", borderRadius: 10, border: "1px solid var(--border)", backgroundColor: "var(--muted-30)" }}>
            <button
              onClick={() => setDrawerOpen(true)}
              aria-label="Open clients"
              style={{ padding: 8, borderRadius: 8, border: "1px solid var(--border)", backgroundColor: "var(--background)", cursor: "pointer", color: "var(--foreground)", display: "flex", flexShrink: 0 }}
            >
              <PanelLeft style={{ width: 16, height: 16 }} />
            </button>
            <div style={{ flex: 1, minWidth: 0, fontSize: "var(--text-sm)", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {active ? active.brand_name : "Pick a client"}
            </div>
          </div>
        )}
        <div className="px-4 md:px-6 py-5 md:py-6">
          {err && <div className="mb-4"><ErrorNote>{err}</ErrorNote></div>}
          {active ? (
            <RecordDetail key={active.customer_id} customer={active} onChanged={loadCustomers} />
          ) : (
            <Section title="Pick a client" icon={<BookMarked style={{ width: 16, height: 16, color: ACCENT }} />}>
              <p style={helpStyle}>
                Each client has one record: what every read measured, the confirmed truth set, the voice profile,
                monitoring snapshots, market findings, every publication and what it moved, and the events in between.
                Every entry names its source.
              </p>
            </Section>
          )}
        </div>
      </main>
    </div>
  );
}
